import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import {
  PortWithState,
  StartSessionDto,
  RelayPortSession,
} from '../../../models/port.model';
import { PortService } from '../../../services/port.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-port-details-stub',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './port-details-stub.html',
  styleUrl: './port-details-stub.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortDetailsStubComponent implements OnDestroy {
  @Input({ required: true }) port!: PortWithState;

  isLoading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private portService: PortService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasActiveSession(): boolean {
    return !!this.port.activeSession;
  }

  get sessionTypeLabel(): string {
    if (!this.port.activeSession) return '';
    return this.port.activeSession.type === 'WORKING' ? 'Рабочая' : 'Техническая';
  }

  startSession(type: 'WORKING' | 'TECHNICAL'): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.cdr.markForCheck();

    const dto: StartSessionDto = { type };

    this.portService
      .startSession(this.port.id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (session: RelayPortSession) => {
          this.isLoading = false;
          this.port.activeSession = {
            id: session.id ?? '',
            type: session.type,
            startedAt: session.startedAt,
          };
          this.port.currentState = {
            id: session.id ?? '',
            relayPortId: this.port.id,
            state: session.type === 'WORKING' ? 'ON' : 'OFF',
            recordedAt: session.startedAt,
          };
          this.toastService.success(
            session.type === 'WORKING'
              ? 'Рабочая сессия запущена'
              : 'Техническая сессия запущена',
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.toastService.error('Не удалось запустить сессию');
          this.cdr.markForCheck();
        },
      });
  }

  endSession(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.cdr.markForCheck();

    this.portService
      .endSession(this.port.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.port.activeSession = null;
          this.port.currentState = null;
          this.toastService.success('Сессия завершена');
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.toastService.error('Не удалось завершить сессию');
          this.cdr.markForCheck();
        },
      });
  }
}
